import { useEffect, useState } from "react";
import {
  Download,
  Filter,
  Search,
  FileText,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  Receipt,
  Users,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { PageNavigation } from "../components/ui/page-navigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// Revenue/Claims Dashboard later todo with patient, doctor, cpt codes, insurance method
const MOCK_BILLING_HISTORY = [
  {
    id: "INV-2025-00123",
    date: "2025-11-20 10:30 AM",
    subscriptionType: "Premium Plan (3 Seats)",
    method: "Credit Card ending in 4242",
    amount: 450.0,
    status: "Paid",
  },
  {
    id: "INV-2025-00124",
    date: "2025-10-20 10:30 AM",
    subscriptionType: "Premium Plan (3 Seats)",
    method: "Credit Card ending in 4242",
    amount: 450.0,
    status: "Paid",
  },
  {
    id: "INV-2025-00125",
    date: "2025-09-20 10:30 AM",
    subscriptionType: "Basic Plan (1 Seat)",
    method: "Venmo",
    amount: 150.0,
    status: "Paid",
  },
  {
    id: "INV-2025-00126",
    date: "2025-12-20 10:30 AM",
    subscriptionType: "Premium Plan (3 Seats)",
    method: "Credit Card ending in 4242",
    amount: 450.0,
    status: "Pending",
  },
  {
    id: "INV-2025-00127",
    date: "2025-12-20 10:30 AM",
    subscriptionType: "Premium Plan (3 Seats)",
    method: "Credit Card ending in 4242",
    amount: 450.0,
    status: "Declined",
  },
];

function getStatusBadgeClasses(status) {
  if (status === "Paid") {
    return "bg-emerald-50 text-emerald-700 border border-emerald-100";
  }
  if (status === "Pending") {
    return "bg-amber-50 text-amber-700 border border-amber-100";
  }
  return "bg-red-50 text-red-700 border border-red-100";
}

function getStatusIcon(status) {
  if (status === "Paid") return <CheckCircle className="w-3 h-3 mr-1" />;
  if (status === "Pending") return <Clock className="w-3 h-3 mr-1" />;
  return <XCircle className="w-3 h-3 mr-1" />;
}

export default function BillingHistory() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const { toast } = useToast();

  useEffect(() => {
    document.title = "Billing History - Seismic Connect";
  }, []);

  const handleExportCSV = () => {
    if (filteredRows.length === 0) {
      toast({ title: "No Data", description: "There is no data to export.", variant: "destructive" });
      return;
    }
    const headers = ["Transaction ID", "Date", "Subscription Type", "Payment Method", "Amount", "Status"];
    const csvContent = [
      headers.join(","),
      ...filteredRows.map(row => 
        `"${row.id}","${row.date}","${row.subscriptionType}","${row.method}","${row.amount}","${row.status}"`
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "billing_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({ title: "Export Successful", description: "Your CSV file has been downloaded." });
  };

  const handleDownloadFile = (type, row) => {
    const content = `${type.toUpperCase()}\n\nTransaction ID: ${row.id}\nDate: ${row.date}\nAmount: $${row.amount.toFixed(2)}\nStatus: ${row.status}\nMethod: ${row.method}\nSubscription: ${row.subscriptionType}`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${type}_${row.id}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({ title: "Download Started", description: `Your ${type} is downloading.` });
  };

  const filteredRows = MOCK_BILLING_HISTORY.filter((row) => {
    const matchesSearch =
      search.trim().length === 0 ||
      row.id.toLowerCase().includes(search.toLowerCase()) ||
      row.subscriptionType.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || row.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalAmount = filteredRows.reduce((sum, row) => sum + row.amount, 0);

  return (
    <div className="px-4 pb-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageNavigation
          title="Billing History"
          subtitle="Review past invoices, payment status, and transaction details"
          showDate={true}
        />

        {/* Filters + Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          {/* Filters card */}
          <Card className="lg:col-span-2 shadow-md border border-gray-100" data-tour="billing-history-filters">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-600" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <Label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1">
                    <Search className="w-3 h-3" />
                    Search (transaction id, plan type)
                  </Label>
                  <Input
                    placeholder="Search transactions..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-600 mb-1 block">
                    Status
                  </Label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full h-9 text-sm border border-gray-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  >
                    <option>All</option>
                    <option>Paid</option>
                    <option>Pending</option>
                    <option>Declined</option>
                  </select>
                </div>
                <div className="flex items-end justify-start md:justify-end">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 h-9 border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={handleExportCSV}
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary card */}
          <Card
            className="lg:col-span-1 shadow-lg border-0 bg-gradient-to-br from-gray-900 via-slate-800 to-black text-white relative overflow-hidden"
            data-tour="billing-history-summary"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 rounded-full opacity-10 blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                Monthly Spend Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 mt-2 relative z-10">
              <p className="text-4xl font-extrabold tracking-tight text-white drop-shadow-sm">
                ${totalAmount.toFixed(2)}
              </p>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Spend (Filtered)</p>
            </CardContent>
          </Card>
          
          {/* Seat-based Cost Breakdown */}
          <Card
            className="lg:col-span-1 shadow-lg border border-slate-200 bg-white/60 backdrop-blur-md relative overflow-hidden"
            data-tour="billing-history-seat-breakdown"
          >
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
            <CardHeader className="pb-2 border-b border-slate-100 mb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
                <Users className="w-4 h-4 text-blue-500" />
                Seat-based Cost Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 relative z-10">
               <div className="flex justify-between items-center text-sm group">
                 <span className="text-slate-500 font-medium group-hover:text-slate-800 transition-colors">Base Plan (Premium)</span>
                 <span className="font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">$150.00</span>
               </div>
               <div className="flex justify-between items-center text-sm group">
                 <span className="text-slate-500 font-medium group-hover:text-slate-800 transition-colors">Additional Seats (2)</span>
                 <span className="font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">$300.00</span>
               </div>
               <div className="pt-3 mt-3 border-t border-slate-100 flex justify-between items-center">
                 <span className="font-bold text-slate-800 text-sm uppercase tracking-wide">Total Expected</span>
                 <span className="font-extrabold text-blue-600 text-lg">$450.00 <span className="text-sm font-medium text-slate-500">/mo</span></span>
               </div>
            </CardContent>
          </Card>
        </div>

        {/* Billing history table */}
        <Card className="shadow-lg border border-gray-100 overflow-hidden" data-tour="billing-history-table">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-700" />
                <CardTitle className="text-base text-gray-800">
                  Transaction List
                </CardTitle>
              </div>
              <span className="text-xs text-gray-500">
                {filteredRows.length} record(s)
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 font-medium">Transaction ID</th>
                    <th className="px-4 py-3 font-medium">Date/Time</th>
                    <th className="px-4 py-3 font-medium">Subscription Type</th>
                    <th className="px-4 py-3 font-medium">Payment Method</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0 border-gray-100 hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {row.id}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.date}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.subscriptionType}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.method}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-semibold">
                        ${row.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium ${getStatusBadgeClasses(
                            row.status
                          )}`}
                        >
                          {getStatusIcon(row.status)}
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex items-center gap-2">
                        <button
                          className="text-gray-500 hover:text-blue-600 transition flex items-center gap-1"
                          title="Download Invoice"
                          onClick={() => handleDownloadFile("invoice", row)}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          className="text-gray-500 hover:text-blue-600 transition flex items-center gap-1"
                          title="Download Receipt"
                          onClick={() => handleDownloadFile("receipt", row)}
                        >
                          <Receipt className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-sm text-gray-500"
                      >
                        No billing records match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
